import { lazy } from 'react';

// ============================================
// LAZY LOADED COMPONENTS - Code Splitting
// ============================================

// Authentication
const Login = lazy(() => import('../../../authentication/Login/Login'));
const CustomerLogin = lazy(() => import('../../../authentication/CustomerLogin/CustomerLogin'));
const LogOut = lazy(() => import('../../../authentication/Logout/LogOut'));
const Register = lazy(() => import('../../../authentication/Register/Register'));
const PublicClientRegistration = lazy(() => import('../../../public/ClientRegistration/PublicClientRegistration'));
const UserProfile = lazy(() => import('../../../authentication/Profile/UserProfile'));
const ForgotPassword = lazy(() => import('../../../authentication/ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('../../../authentication/ResetPassword/ResetPassword'));

// Analytics Module
const IRPAnalyticsDashboard = lazy(() => import('../../../modules/analytics/pages/IRP'));

// Product Module
const ProductMaster = lazy(() => import('../../../modules/master-data/Product/pages/Master/ProductMaster'));
const AddProduct = lazy(() => import('../../../modules/master-data/Product/pages/Product/Add/AddProduct'));
const ProductDetails = lazy(() => import('../../../modules/master-data/Product/pages/Product/Detail/ProductDetail'));
const AddCategory = lazy(() => import('../../../modules/master-data/Product/pages/Category/Add/AddCategory'));
const CategoryDetail = lazy(() => import('../../../modules/master-data/Product/pages/Category/Detail/CategoryDetail'));
const AddSubCategory = lazy(() => import('../../../modules/master-data/Product/pages/SubCategory/Add/AddSubCategory'));
const SubCategoryDetail = lazy(() => import('../../../modules/master-data/Product/pages/SubCategory/Detail/SubCategoryDetail'));
const PriceManageMaster = lazy(() => import('../../../modules/master-data/Product/pages/PriceManage/Master/PriceManageMaster'));
const DiscountManageMaster = lazy(() => import('../../../modules/master-data/Product/pages/DiscountManage/Master/DiscountManageMaster'));
const StockConfigPage = lazy(() => import('../../../modules/master-data/Product/pages/StockConfig/StockConfigPage'));

// User Module
const SalesUserMaster = lazy(() => import('../../../modules/master-data/User/pages/SalesUser/SalesUserMaster'));
const BackendStaffMaster = lazy(() => import('../../../modules/master-data/User/pages/BackendStaff/BackendStaffMaster'));
const UserDetail = lazy(() => import('../../../modules/master-data/User/pages/Shared/UserDetail'));
const UserHierarchy = lazy(() => import('../../../modules/master-data/User/pages/Shared/UserHierarchy'));
const AddUser = lazy(() => import('../../../modules/master-data/User/pages/Shared/AddUser'));

// Dropdown Management Module
const DropdownManagementMaster = lazy(() => import('../../../modules/master-data/DropdownManagement/pages/Master/DropdownManagementMaster'));
const DropdownOptionForm = lazy(() => import('../../../modules/master-data/DropdownManagement/pages/DropdownOptionForm'));

// Keyboard Shortcut Module
const KeyboardShortcutMaster = lazy(() => import('../../../modules/master-data/KeyboardShortcut/pages/Master/KeyboardShortcutMaster'));
const AddKeyboardShortcut = lazy(() => import('../../../modules/master-data/KeyboardShortcut/pages/Add/AddKeyboardShortcut'));

// Authorization Module
const RoleMaster = lazy(() => import('../../../authorization/RoleManagement/RoleMaster').then(m => ({ default: m.RoleMaster })));
const AddRole = lazy(() => import('../../../authorization/RoleManagement/AddRole'));
const PermissionMaster = lazy(() => import('../../../authorization/PermissionManagement/PermissionMaster').then(m => ({ default: m.PermissionMaster })));

// Postal Module
const PostalMaster = lazy(() => import('../../../modules/master-data/Postal/pages/Master/PostalMaster'));
const AddPostal = lazy(() => import('../../../modules/master-data/Postal/pages/Add/AddPostal'));
const PostalDetail = lazy(() => import('../../../modules/master-data/Postal/pages/Detail/PostalDetail'));

// Territory Master Module
const TerritoryMaster = lazy(() => import('../../../modules/master-data/TerritoryMaster/pages/Master/TerritoryMaster'));

// AppConfig Module
const AppConfigMaster = lazy(() => import('../../../modules/master-data/AppConfig/pages/Master/AppConfigMaster'));
const AddAppConfig = lazy(() => import('../../../modules/master-data/AppConfig/pages/Add/AddAppConfig'));
const BannersPage = lazy(() => import('../../../modules/master-data/AppConfig/pages/Banners/BannersPage'));
const FAQPage = lazy(() => import('../../../modules/master-data/AppConfig/pages/FAQ/FAQPage'));
const VideosPage = lazy(() => import('../../../modules/master-data/AppConfig/pages/Videos/VideosPage'));
const CataloguesPage = lazy(() => import('../../../modules/master-data/AppConfig/pages/Catalogues/CataloguesPage'));
const ContactUsPage = lazy(() => import('../../../modules/master-data/AppConfig/pages/ContactUs/ContactUsPage'));

// Holiday Module
const HolidayMaster = lazy(() => import('../../../modules/master-data/Holiday/pages/Master/HolidayMaster'));
const AddHoliday = lazy(() => import('../../../modules/master-data/Holiday/pages/Add/AddHoliday'));

// Tutorial Video Module (Read-only)
const TutorialVideoList = lazy(() => import('../../../modules/master-data/TutorialVideo/pages/TutorialVideoList'));

// Customer Module
const CustomerMaster = lazy(() => import('../../../modules/master-data/Customer/pages/Master/CustomerMaster'));
const CustomerInsights = lazy(() => import('../../../modules/master-data/Customer/pages/Shared').then(m => ({ default: m.CustomerInsights })));

// Customer Network - Primary
const PrimaryMaster = lazy(() => import('../../../modules/master-data/Customer/pages/PrimaryNetwork').then(m => ({ default: m.PrimaryMaster })));
const PrimaryAdd = lazy(() => import('../../../modules/master-data/Customer/pages/PrimaryNetwork').then(m => ({ default: m.PrimaryAdd })));
const PrimaryEdit = lazy(() => import('../../../modules/master-data/Customer/pages/PrimaryNetwork').then(m => ({ default: m.PrimaryEdit })));
const PrimaryDetail = lazy(() => import('../../../modules/master-data/Customer/pages/PrimaryNetwork').then(m => ({ default: m.PrimaryDetail })));

// Customer Network - Secondary
const SecondaryMaster = lazy(() => import('../../../modules/master-data/Customer/pages/SecondaryNetwork').then(m => ({ default: m.SecondaryMaster })));
const SecondaryAdd = lazy(() => import('../../../modules/master-data/Customer/pages/SecondaryNetwork').then(m => ({ default: m.SecondaryAdd })));
const SecondaryEdit = lazy(() => import('../../../modules/master-data/Customer/pages/SecondaryNetwork').then(m => ({ default: m.SecondaryEdit })));
const SecondaryDetail = lazy(() => import('../../../modules/master-data/Customer/pages/SecondaryNetwork').then(m => ({ default: m.SecondaryDetail })));

// Customer Network - Direct
const DirectMaster = lazy(() => import('../../../modules/master-data/Customer/pages/DirectNetwork').then(m => ({ default: m.DirectMaster })));
const DirectAdd = lazy(() => import('../../../modules/master-data/Customer/pages/DirectNetwork').then(m => ({ default: m.DirectAdd })));
const DirectEdit = lazy(() => import('../../../modules/master-data/Customer/pages/DirectNetwork').then(m => ({ default: m.DirectEdit })));
const DirectDetail = lazy(() => import('../../../modules/master-data/Customer/pages/DirectNetwork').then(m => ({ default: m.DirectDetail })));

// Customer Network - Influencer
const InfluencerMaster = lazy(() => import('../../../modules/master-data/Customer/pages/InfluencerNetwork').then(m => ({ default: m.InfluencerMaster })));
const InfluencerAdd = lazy(() => import('../../../modules/master-data/Customer/pages/InfluencerNetwork').then(m => ({ default: m.InfluencerAdd })));
const InfluencerEdit = lazy(() => import('../../../modules/master-data/Customer/pages/InfluencerNetwork').then(m => ({ default: m.InfluencerEdit })));
const InfluencerDetail = lazy(() => import('../../../modules/master-data/Customer/pages/InfluencerNetwork').then(m => ({ default: m.InfluencerDetail })));

// Session Management Module
const SessionManagementMaster = lazy(() => import('../../../modules/master-data/SessionManagement/pages/Master/SessionManagementMaster'));
const SessionDetail = lazy(() => import('../../../modules/master-data/SessionManagement/pages/Detail/SessionDetail'));

// E-Catalogue Module
const CatalogueMaster = lazy(() => import('../../../modules/common-modules/Catalogue/pages/Master/CatalogueMaster'));
const AddCatalogue = lazy(() => import('../../../modules/common-modules/Catalogue/pages/Add/AddCatalogue'));
const CatalogueDetail = lazy(() => import('../../../modules/common-modules/Catalogue/pages/Detail/CatalogueDetail'));
const CatalogueInsights = lazy(() => import('../../../modules/common-modules/Catalogue/pages/Insights/CatalogueInsights'));

// Announcement Module
const AnnouncementMaster = lazy(() => import('../../../modules/common-modules/Announcement/pages/Master/AnnouncementMaster'));
const AddAnnouncement = lazy(() => import('../../../modules/common-modules/Announcement/pages/Add/AddAnnouncement'));
const AnnouncementDetail = lazy(() => import('../../../modules/common-modules/Announcement/pages/Detail/AnnouncementDetail'));

// Notification Module
const NotificationMaster = lazy(() => import('../../../modules/common-modules/Notification/pages/Master/NotificationMaster'));

// Survey Module
const SurveyMaster = lazy(() => import('../../../modules/common-modules/Survey/pages/Master/SurveyMaster'));
const AddSurvey = lazy(() => import('../../../modules/common-modules/Survey/pages/Add/AddSurvey'));
const SurveyDetail = lazy(() => import('../../../modules/common-modules/Survey/pages/Detail/SurveyDetail'));

// Support Module
const SupportMaster = lazy(() => import('../../../modules/common-modules/Support/pages/Master/SupportMaster'));
const AddSupport = lazy(() => import('../../../modules/common-modules/Support/pages/Add/AddSupport'));
const SupportDetail = lazy(() => import('../../../modules/common-modules/Support/pages/Detail/SupportDetail'));
const SupportInsights = lazy(() => import('../../../modules/common-modules/Support/pages/Insights/SupportInsights'));

// Accounts Module
const AccountsMaster = lazy(() => import('../../../modules/common-modules/Accounts/pages/Master/AccountsMaster'));
const InvoiceDetail = lazy(() => import('../../../modules/common-modules/Accounts/pages/Detail/InvoiceDetail'));

// Lead Module (Lead Management System)
// SFA/LMS removed 2026-06-17 — see SFA_NUCLEAR_REMOVAL.md
// LeadMaster, AddLead, LeadDetail removed — src/modules/departments/lead-management-system deleted
// const LeadMaster = lazy(() => import('../../../modules/departments/lead-management-system/lead/pages/Master/LeadMaster'));
// const AddLead = lazy(() => import('../../../modules/departments/lead-management-system/lead/pages/Add/AddLead'));
// const LeadDetail = lazy(() => import('../../../modules/departments/lead-management-system/lead/pages/Detail/LeadDetail'));


// Chat
const ChatMaster = lazy(() => import('../../../modules/common-modules/Chat/pages/Master/ChatMaster'));

// BasiqGPT (AI Assistant)
const BasiqGPTChat = lazy(() => import('../../../modules/common-modules/BasiqGPT/pages/BasiqGPTChat'));

// Admin Basiq360 Module
const AdminDashboard = lazy(() => import('../../../admin-basiq360/pages/Dashboard/AdminDashboard'));
const AnalyticsDashboard = lazy(() => import('../../../admin-basiq360/pages/Analytics/AnalyticsDashboard'));
const ClientMaster = lazy(() => import('../../../admin-basiq360/pages/Clients/ClientMaster'));
const ClientForm = lazy(() => import('../../../admin-basiq360/pages/Clients/ClientForm'));
const ClientDetail = lazy(() => import('../../../admin-basiq360/pages/Clients/ClientDetail'));
const ModuleMaster = lazy(() => import('../../../admin-basiq360/pages/Modules/ModuleMaster'));
const SchedulerMaster = lazy(() => import('../../../admin-basiq360/pages/Scheduler/SchedulerMaster'));
const AdminSettings = lazy(() => import('../../../admin-basiq360/pages/Settings/AdminSettings'));

// Admin Communications Module
const CommunicationMaster = lazy(() => import('../../../admin-basiq360/pages/Communications/CommunicationMaster'));
const CredentialForm = lazy(() => import('../../../admin-basiq360/pages/Communications/CredentialForm'));
const TemplateForm = lazy(() => import('../../../admin-basiq360/pages/Communications/TemplateForm'));

// Loyalty Admin Module
const LoyaltyAnalyticsDashboard = lazy(() => import('../../../modules/departments/loyalty/pages/Dashboard/LoyaltyAnalyticsDashboard'));
const LoyaltyInfluencerMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Influencers/InfluencerMaster'));
const LoyaltyInfluencerAdd = lazy(() => import('../../../modules/departments/loyalty/pages/Influencers/InfluencerAdd'));
const LoyaltyInfluencerEdit = lazy(() => import('../../../modules/departments/loyalty/pages/Influencers/InfluencerEdit'));
const LoyaltyInfluencerDetail = lazy(() => import('../../../modules/departments/loyalty/pages/Influencers/InfluencerDetail'));
const LoyaltyRedemptionMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Redemptions/RedemptionMaster'));
const LoyaltyFraudQueue = lazy(() => import('../../../modules/departments/loyalty/pages/Fraud/FraudQueueMaster'));
const LoyaltyCouponMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Coupons/CouponMaster'));
const LoyaltyCouponBatchAdd = lazy(() => import('../../../modules/departments/loyalty/pages/Coupons/CouponBatchAdd'));
const LoyaltyGiftMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Gifts/GiftMaster'));
const LoyaltyGiftAddEdit = lazy(() => import('../../../modules/departments/loyalty/pages/Gifts/GiftAddEdit'));
const GamesMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Games/GamesMaster'));
const LoyaltyCampaignMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Campaigns/CampaignMaster'));
const LoyaltyCampaignAddEdit = lazy(() => import('../../../modules/departments/loyalty/pages/Campaigns/CampaignAddEdit'));
const LoyaltyOcrReceiptMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Receipts/OcrReceiptMaster'));
const LoyaltyBonusSchemeMaster = lazy(() => import('../../../modules/departments/loyalty/pages/BonusSchemes/BonusSchemeMaster'));
const LoyaltyBonusSchemeAddEdit = lazy(() => import('../../../modules/departments/loyalty/pages/BonusSchemes/BonusSchemeAddEdit'));
// LoyaltyRewardSettingsAdmin import removed 2026-06-20 — UI moved into BonusSchemeMaster > "Bonus Settings" tab.
const LoyaltyTargetingDashboard = lazy(() => import('../../../modules/departments/loyalty/pages/Targeting/TargetingDashboard'));
const LoyaltyReferralMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Referrals/ReferralMaster'));
const LoyaltyPointCategoryAdmin = lazy(() => import('../../../modules/departments/loyalty/pages/PointCategory/PointCategoryAdmin'));
const WorkShowcaseMaster = lazy(() => import('../../../modules/departments/loyalty/pages/WorkShowcase/WorkShowcaseMaster'));
const LoyaltyKycReviewQueue = lazy(() => import('../../../modules/departments/loyalty/pages/KYC/KycReviewQueue'));
const LoyaltyKycSettings = lazy(() => import('../../../modules/departments/loyalty/pages/KYC/KycSettings'));
const LoyaltyPayoutMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Payouts/PayoutMaster'));
const LoyaltyTierConfigAdmin = lazy(() => import('../../../modules/departments/loyalty/pages/TierConfig/TierConfigAdmin'));
const LoyaltyPointHistoryMaster = lazy(() => import('../../../modules/departments/loyalty/pages/PointHistory/PointHistoryMaster'));
const LeaderboardMaster = lazy(() => import('../../../modules/departments/loyalty/pages/Leaderboard/LeaderboardMaster'));
const Gamification = lazy(() => import('../../../modules/departments/loyalty/pages/Gamification/Gamification'));

// Loyalty - Channel Partner Admin
const ChannelPartnerMaster = lazy(() => import('../../../modules/departments/loyalty/pages/ChannelPartners/ChannelPartnerMaster'));
const ChannelPartnerAdd = lazy(() => import('../../../modules/departments/loyalty/pages/ChannelPartners/ChannelPartnerAdd'));
const ChannelPartnerEdit = lazy(() => import('../../../modules/departments/loyalty/pages/ChannelPartners/ChannelPartnerEdit'));
const ChannelPartnerDetail = lazy(() => import('../../../modules/departments/loyalty/pages/ChannelPartners/ChannelPartnerDetail'));

// Not Found Page
const NotFoundPage = lazy(() => import('./NotFoundPage'));

// ============================================
// ROUTE DEFINITIONS
// ============================================

interface RouteObject {
  isNotFound?: boolean;
  path: string;
  component: React.ComponentType<Record<string, unknown>>;
  exact?: boolean;
}

const authProtectedRoutes: Array<RouteObject> = [
  // User Profile
  { path: "/user-profile", component: UserProfile },

  // Analytics Module Routes
  { path: "/analytics/influencer-reward-program", component: IRPAnalyticsDashboard },

  // Product Module Routes
  { path: "/product", component: ProductMaster },
  { path: "/product/list", component: ProductMaster },
  { path: "/product/insights", component: ProductMaster },
  { path: "/product/point-categories", component: ProductMaster },
  { path: "/product/add/:type", component: AddProduct },
  { path: "/product/add", component: AddProduct },
  { path: "/product/edit/:id", component: AddProduct },
  { path: "/product/list/edit/product/:id", component: AddProduct },
  { path: "/product/details/:id", component: ProductDetails },
  { path: "/product/list/details/:id", component: ProductDetails },

  // Category Routes
  { path: "/category/list", component: ProductMaster },
  { path: "/category/add", component: AddCategory },
  { path: "/category/edit/:id", component: AddCategory },
  { path: "/category/details/:id", component: CategoryDetail },

  // SubCategory Routes
  { path: "/subcategory/list", component: ProductMaster },
  { path: "/subcategory/add", component: AddSubCategory },
  { path: "/subcategory/edit/:id", component: AddSubCategory },
  { path: "/subcategory/details/:id", component: SubCategoryDetail },

  // Price Management Route
  { path: "/product/price-manage", component: PriceManageMaster },

  // Discount Management Route
  { path: "/product/discount-manage", component: DiscountManageMaster },

  // Stock Configuration Route
  { path: "/product/stock-config", component: StockConfigPage },

  // User Module Routes - Backend Staff
  { path: "/user/backend", component: BackendStaffMaster },
  { path: "/user/backend/list", component: BackendStaffMaster },
  { path: "/user/backend/add", component: AddUser },
  { path: "/user/backend/edit/:id", component: AddUser },
  { path: "/user/backend/details/:id", component: UserDetail },
  { path: "/user/backend/insights", component: BackendStaffMaster },

  // User Module Routes - Sales User
  { path: "/user/sales", component: SalesUserMaster },
  { path: "/user/sales/list", component: SalesUserMaster },
  { path: "/user/sales/add", component: AddUser },
  { path: "/user/sales/edit/:id", component: AddUser },
  { path: "/user/sales/details/:id", component: UserDetail },
  { path: "/user/sales/insights", component: SalesUserMaster },

  // User Module Routes - Shared
  { path: "/user/hierarchy", component: UserHierarchy },

  // Dropdown Management Module Routes
  { path: "/dropdown-management", component: DropdownManagementMaster },
  { path: "/dropdown-management/options", component: DropdownManagementMaster },
  { path: "/dropdown-management/options/add", component: DropdownOptionForm },
  { path: "/dropdown-management/options/edit/:id", component: DropdownOptionForm },

  // Keyboard Shortcut Module Routes
  { path: "/keyboard-shortcut", component: KeyboardShortcutMaster },
  { path: "/keyboard-shortcut/add", component: AddKeyboardShortcut },
  { path: "/keyboard-shortcut/edit/:id", component: AddKeyboardShortcut },

  // Authorization Module Routes
  { path: "/authorization/roles", component: RoleMaster },
  { path: "/authorization/roles/add", component: AddRole },
  { path: "/authorization/roles/edit/:id", component: AddRole },
  { path: "/authorization/permissions", component: PermissionMaster },

  // Postal Module Routes
  { path: "/postal", component: PostalMaster },
  { path: "/postal/add", component: AddPostal },
  { path: "/postal/edit/:id", component: AddPostal },
  { path: "/postal/details/:id", component: PostalDetail },

  // Territory Master Module Routes
  { path: "/territory-master", component: TerritoryMaster },

  // AppConfig Module Routes
  { path: "/app-config", component: AppConfigMaster },
  { path: "/app-config/add", component: AddAppConfig },
  { path: "/app-config/edit/:id", component: AddAppConfig },
  { path: "/app-config/banners", component: BannersPage },
  { path: "/app-config/faq", component: FAQPage },
  { path: "/app-config/videos", component: VideosPage },
  { path: "/app-config/catalogues", component: CataloguesPage },
  { path: "/app-config/contact", component: ContactUsPage },

  // Holiday Module Routes
  { path: "/holiday", component: HolidayMaster },
  { path: "/holiday/add", component: AddHoliday },
  { path: "/holiday/edit/:id", component: AddHoliday },

  // Tutorial Video Module Routes (Read-only)
  { path: "/tutorial-videos", component: TutorialVideoList },

  // Customer Module Routes
  { path: "/customer", component: CustomerMaster },
  { path: "/customer/insights", component: CustomerInsights },

  // Customer Network Routes - Primary Network
  { path: "/customer/primary-network", component: PrimaryMaster },
  { path: "/customer/primary-network/add", component: PrimaryAdd },
  { path: "/customer/primary-network/edit/:id", component: PrimaryEdit },
  { path: "/customer/primary-network/details/:id", component: PrimaryDetail },

  // Customer Network Routes - Secondary Network
  { path: "/customer/secondary-network", component: SecondaryMaster },
  { path: "/customer/secondary-network/add", component: SecondaryAdd },
  { path: "/customer/secondary-network/edit/:id", component: SecondaryEdit },
  { path: "/customer/secondary-network/details/:id", component: SecondaryDetail },

  // Customer Network Routes - Direct Network
  { path: "/customer/direct-network", component: DirectMaster },
  { path: "/customer/direct-network/add", component: DirectAdd },
  { path: "/customer/direct-network/edit/:id", component: DirectEdit },
  { path: "/customer/direct-network/details/:id", component: DirectDetail },

  // Customer Network Routes - Influencer Network
  { path: "/customer/influencer-network", component: InfluencerMaster },
  { path: "/customer/influencer-network/add", component: InfluencerAdd },
  { path: "/customer/influencer-network/edit/:id", component: InfluencerEdit },
  { path: "/customer/influencer-network/details/:id", component: InfluencerDetail },

  // Session Management Module Routes
  { path: "/session-management", component: SessionManagementMaster },
  { path: "/session-management/:id", component: SessionDetail },

  // Notification Module Routes
  { path: "/notifications", component: NotificationMaster },

  // Announcement Module Routes
  { path: "/announcement", component: AnnouncementMaster },
  { path: "/announcement/list", component: AnnouncementMaster },
  { path: "/announcement/add", component: AddAnnouncement },
  { path: "/announcement/edit/:id", component: AddAnnouncement },
  { path: "/announcement/details/:id", component: AnnouncementDetail },
  { path: "/announcement/insights", component: AnnouncementMaster },

  // Survey Module Routes
  { path: "/survey", component: SurveyMaster },
  { path: "/survey/list", component: SurveyMaster },
  { path: "/survey/add", component: AddSurvey },
  { path: "/survey/edit/:id", component: AddSurvey },
  { path: "/survey/details/:id", component: SurveyDetail },
  { path: "/survey/insights", component: SurveyMaster },

  // Support Module Routes
  { path: "/support", component: SupportMaster },
  { path: "/support/pending", component: SupportMaster },
  { path: "/support/inprocess", component: SupportMaster },
  { path: "/support/closed", component: SupportMaster },
  { path: "/support/insights", component: SupportInsights },
  { path: "/support/add", component: AddSupport },
  { path: "/support/edit/:id", component: AddSupport },
  { path: "/support/details/:id", component: SupportDetail },

  // E-Catalogue Module Routes
  { path: "/e-catalogue", component: CatalogueMaster },
  { path: "/e-catalogue/list", component: CatalogueMaster },
  { path: "/e-catalogue/add", component: AddCatalogue },
  { path: "/e-catalogue/edit/:id", component: AddCatalogue },
  { path: "/e-catalogue/details/:id", component: CatalogueDetail },
  { path: "/e-catalogue/insights", component: CatalogueInsights },

  // Accounts Module Routes
  { path: "/accounts", component: AccountsMaster },
  { path: "/accounts/invoices", component: AccountsMaster },
  { path: "/accounts/payments", component: AccountsMaster },
  { path: "/accounts/invoice/:id", component: InvoiceDetail },
  { path: "/accounts/insights", component: AccountsMaster },

  // Lead Module Routes
  // SFA/LMS removed 2026-06-17 — see SFA_NUCLEAR_REMOVAL.md
  // Lead routes removed — LeadMaster, AddLead, LeadDetail deleted
  // { path: "/leads", component: LeadMaster },
  // { path: "/leads/list", component: LeadMaster },
  // { path: "/leads/add", component: AddLead },
  // { path: "/leads/edit/:id", component: AddLead },
  // { path: "/leads/details/:id", component: LeadDetail },

  // Chat
  { path: "/chat", component: ChatMaster },

  // BasiqGPT (AI Assistant)
  { path: "/basiq-gpt", component: BasiqGPTChat },

  // Admin Basiq360 Routes
  { path: "/admin/dashboard", component: AdminDashboard },
  { path: "/admin/analytics", component: AnalyticsDashboard },
  { path: "/admin/clients", component: ClientMaster },
  { path: "/admin/trials", component: ClientMaster },
  { path: "/admin/clients/add", component: ClientForm },
  { path: "/admin/clients/:id", component: ClientDetail },
  { path: "/admin/clients/:id/edit", component: ClientForm },
  { path: "/admin/modules", component: ModuleMaster },
  { path: "/admin/modules/add", component: ModuleMaster },
  { path: "/admin/modules/:id", component: ModuleMaster },
  { path: "/admin/modules/:id/edit", component: ModuleMaster },
  { path: "/admin/scheduler", component: SchedulerMaster },
  { path: "/admin/scheduler/add", component: SchedulerMaster },
  { path: "/admin/scheduler/:id", component: SchedulerMaster },
  { path: "/admin/scheduler/:id/edit", component: SchedulerMaster },
  { path: "/admin/roles", component: RoleMaster },
  { path: "/admin/roles/add", component: AddRole },
  { path: "/admin/roles/edit/:id", component: AddRole },
  { path: "/admin/permissions", component: PermissionMaster },
  { path: "/admin/settings", component: AdminSettings },
  { path: "/admin/settings/general", component: AdminSettings },
  { path: "/admin/settings/security", component: AdminSettings },
  { path: "/admin/settings/notifications", component: AdminSettings },
  { path: "/admin/settings/system", component: AdminSettings },

  // Loyalty Admin Module Routes
  { path: "/loyalty/analytics", component: LoyaltyAnalyticsDashboard },
  { path: "/loyalty/dashboard", component: LoyaltyAnalyticsDashboard },
  { path: "/loyalty/influencers", component: LoyaltyInfluencerMaster },
  { path: "/loyalty/influencers/add", component: LoyaltyInfluencerAdd },
  { path: "/loyalty/influencers/edit/:id", component: LoyaltyInfluencerEdit },
  { path: "/loyalty/influencers/details/:id", component: LoyaltyInfluencerDetail },
  { path: "/loyalty/redemptions", component: LoyaltyRedemptionMaster },
  { path: "/loyalty/payouts", component: LoyaltyPayoutMaster },
  { path: "/loyalty/fraud-queue", component: LoyaltyFraudQueue },
  { path: "/loyalty/coupons", component: LoyaltyCouponMaster },
  { path: "/loyalty/coupons/add", component: LoyaltyCouponBatchAdd },
  { path: "/loyalty/gifts", component: LoyaltyGiftMaster },
  { path: "/loyalty/gifts/add", component: LoyaltyGiftAddEdit },
  { path: "/loyalty/gifts/edit/:id", component: LoyaltyGiftAddEdit },
  { path: "/loyalty/games", component: GamesMaster },
  { path: "/loyalty/campaigns", component: LoyaltyCampaignMaster },
  { path: "/loyalty/campaigns/add", component: LoyaltyCampaignAddEdit },
  { path: "/loyalty/campaigns/edit/:id", component: LoyaltyCampaignAddEdit },
  { path: "/loyalty/receipts", component: LoyaltyOcrReceiptMaster },
  { path: "/loyalty/purchase-based", component: LoyaltyOcrReceiptMaster },
  { path: "/loyalty/work-showcase", component: WorkShowcaseMaster },
  { path: "/loyalty/bonus-schemes", component: LoyaltyBonusSchemeMaster },
  { path: "/loyalty/bonus-schemes/add", component: LoyaltyBonusSchemeAddEdit },
  { path: "/loyalty/bonus-schemes/edit/:id", component: LoyaltyBonusSchemeAddEdit },
  // { path: "/loyalty/reward-settings" } removed 2026-06-20 — see BonusSchemeMaster > "Bonus Settings" tab.
  { path: "/loyalty/targeting", component: LoyaltyTargetingDashboard },
  { path: "/loyalty/referrals", component: LoyaltyReferralMaster },
  { path: "/loyalty/point-categories", component: LoyaltyPointCategoryAdmin },
  { path: "/loyalty/kyc-queue", component: LoyaltyKycReviewQueue },
  { path: "/loyalty/kyc-settings", component: LoyaltyKycSettings },
  { path: "/loyalty/tier-config", component: LoyaltyTierConfigAdmin },
  { path: "/loyalty/point-history", component: LoyaltyPointHistoryMaster },
  { path: "/loyalty/leaderboard", component: LeaderboardMaster },
  { path: "/loyalty/gamification", component: Gamification },

  // Channel Partner Admin Routes
  { path: "/loyalty/channel-partners", component: ChannelPartnerMaster },
  { path: "/loyalty/channel-partners/add", component: ChannelPartnerAdd },
  { path: "/loyalty/channel-partners/edit/:id", component: ChannelPartnerEdit },
  { path: "/loyalty/channel-partners/details/:id", component: ChannelPartnerDetail },

  // Admin Communications Routes
  { path: "/admin/communications", component: CommunicationMaster },
  { path: "/admin/communications/credentials", component: CommunicationMaster },
  { path: "/admin/communications/credentials/add", component: CredentialForm },
  { path: "/admin/communications/credentials/:id/edit", component: CredentialForm },
  { path: "/admin/communications/templates", component: CommunicationMaster },
  { path: "/admin/communications/templates/add", component: TemplateForm },
  { path: "/admin/communications/templates/:id/edit", component: TemplateForm },
  { path: "/admin/communications/logs", component: CommunicationMaster },
];

const publicRoutes: Array<RouteObject> = [
  // Authentication
  { path: "/login", component: Login },
  { path: "/login/customer", component: CustomerLogin },
  { path: "/logout", component: LogOut },
  { path: "/register", component: Register },
  { path: "/forgot-password", component: ForgotPassword },
  { path: "/reset-password", component: ResetPassword },

  // Public Client Registration (Open Link)
  { path: "/client-registration", component: PublicClientRegistration },

  // Not Found Route
  { path: "*", component: NotFoundPage, isNotFound: true },
];

export { authProtectedRoutes, publicRoutes };
